import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, concat, fromEvent, interval, merge, of } from 'rxjs';
import { catchError, filter, first } from 'rxjs/operators';
import { AlertService } from './alert.service';
import { HttpClient } from '@angular/common/http';

export type UpdateStatus =
  | 'idle'
  | 'checking'
  | 'no-update'
  | 'downloading'
  | 'ready'
  | 'failed';

export interface AppVersionInfo {
  version?: string;
  buildTime?: string;
  commit?: string;
}

export interface UpdateState {
  status: UpdateStatus;
  currentHash?: string;
  latestHash?: string;
  currentInfo?: AppVersionInfo; // version.json
  latestInfo?: AppVersionInfo;  // jos haluat erottaa (usein sama kuin currentInfo ennen reloadia)
  hasUpdate: boolean;
  lastCheckedAt?: string;
  error?: string;
}

const initialState: UpdateState = {
  status: 'idle',
  hasUpdate: false
};
@Injectable({providedIn: 'root'})
export class UpdateService {
  private readonly _state$ = new BehaviorSubject<UpdateState>(initialState);
  private reloadInProgress = false;
  readonly state$ = this._state$.asObservable();
  private bc = 'BroadcastChannel' in window ? new BroadcastChannel('app-updates') : null;
  constructor(appRef: ApplicationRef, private updates: SwUpdate, private http:HttpClient, private alertService: AlertService) {

    if (!this.updates.isEnabled) {
      this.patch({ status: 'idle' });
      return;
    }
    this.checkNow();

    // 1) Kuuntele version eventit
    this.updates.versionUpdates.subscribe(evt => {
      if (evt.type === 'VERSION_READY') {
        // Näytä käyttäjälle toast/dialog “Päivitä nyt”
        alertService.versionUpdate();

        this.bc?.postMessage({ type: 'UPDATE_READY' });

        // myös oma store päivittyy tässä
        this.patch({ status: 'ready', hasUpdate: true });
      }
    });

    // Kuuntele viestit muista väleistä
    this.bc?.addEventListener('message', async (e) => {
      switch (e.data?.type) {
        case 'UPDATE_READY':
          this.patch({ status: 'ready', hasUpdate: true });
          break;
        case 'RELOAD_NOW':
          if(this.reloadInProgress) return;
          this.reloadInProgress = true;
          try {
            await this.updates.activateUpdate();
          } finally {
            document.location.reload();
          }
          break;
      }
    });
    // 2) Käynnistä check vasta kun appi on stable, sitten polling
    const appIsStable$ = appRef.isStable.pipe(first(isStable => isStable === true));
    const every15Min$ = interval(15 * 60 * 1000); // tunnin sijaan tiheämmin on usein parempi

    concat(appIsStable$, every15Min$).subscribe(() => {
      this.safeCheck();
    });

    // 3) Tee check myös kun tab tulee takaisin näkyviin tai yhteys palaa
    const onVisible$ = fromEvent(document, 'visibilitychange').pipe(filter(() => !document.hidden));
    const onOnline$ = fromEvent(window, 'online');

    merge(onVisible$, onOnline$).subscribe(() => this.safeCheck());
  }
  // Tarkistaa päivityksen vain ennen sulkemista
  checkForUpdateOnReload() {
    this.updates.checkForUpdate().then(hasUpdate => {
      if (hasUpdate) {
        // Päivitä uusi versio seuraavaan istuntoon
        this.updates.activateUpdate().then(() => console.log('Update will be applied on next reload.'));
      }
    });
  }
  /** Käynnistä manuaalinen tarkistus UI-napista */
  async checkNow() {
    if (!this.updates.isEnabled) return;

    this.patch({ status: 'checking', error: undefined });

    try {
      const hasUpdate = await this.updates.checkForUpdate();

      this.patch({
        status: hasUpdate ? 'downloading' : 'no-update',
        hasUpdate,
        lastCheckedAt: new Date().toISOString()
      });

      // Huom: jos hasUpdate=true, VERSION_READY tulee eventtinä kun lataus valmis
    } catch (e) {
      this.patch({
        status: 'failed',
        error: e instanceof Error ? e.message : String(e),
        lastCheckedAt: new Date().toISOString()
      });
    }
  }

  /** UI-napista: ota uusi versio käyttöön ja reload */
  public async applyUpdateAndReload() {
    if (!this.updates.isEnabled) return;

    await this.updates.activateUpdate();
    document.location.reload();
  }
  public async applyUpdateAndReloadAllTabs() {
    // ilmoita muille heti
    this.bc?.postMessage({ type: 'RELOAD_NOW' });

    // ota uusi käyttöön tässä
    await this.updates.activateUpdate();
    document.location.reload();
  }


  private patch(p: Partial<UpdateState>) {
    this._state$.next({ ...this._state$.value, ...p });
  }
  private async safeCheck() {
    try {
      await this.updates.checkForUpdate();
    } catch (err) {
      console.error('Failed to check for updates:', err);
    }
  }
}
